import { GetEsrsCategoriesQuery_, GetFullEsrsCategoriesQuery_, TaskStatusEnum_Enum_ } from 'models';
import { DataCollectionLevel } from './pieces/DataCollection';
import { QuartersEnums } from './pieces/DisclosureRequirements/Requirement';
import { BooleanEnum } from 'Molecules';
import _ from 'lodash';

export const MULTI_FRAMEWORK = 'multi-framework';

export const environmentDRRefs = [
  'E2-5',
  'E5-5',
  'E5-4',
  'E1-1',
  'E1-3',
  'E1-4',
  'E1-5',
  'E1-6',
  'E4-5',
  'E2-4',
  'E2-6',
  'E5-6',
  'E3-5',
  'E4–SBM-3',
  'E4-2',
  'E4-4',
  'E4-1',
  'E3-1',
  'E4-3',
  'E1-9',
  'E2-1',
  'E5-2',
  'E2-2',
  'E1-GOV-3',
  'E1-7',
  'E3-4',
  'E4-6',
  'E3-3',
  'E5-3',
  'E2-3',
  'E5-1',
  'E3-2',
  'E1–SBM-3',
  'E1-2',
  'E1-8',
];

export const mfStandardRefs = [
  // local db
  'generalOwnWorkforce',
  'generalClimate',
  'generalPollution',
  'generalGeneral',
  'generalBusinessConduct',
  // esrs db
  'Climatechange',
  'general',
  'valueChain',
  'Pollution',
  'Water',
  'Biodiversity',
  'Circular',
  'Workforce',
  'Communities',
  'Consumers',
  'Conduct',
  'mfesrs-oil-and-gas-standard',
];

// When the datapoint doesn't have tags, 'None' is added to the tagCombination field
export const NOTAGCOMBINATION = 'None';

export const getIsWithinYearRange = (startYear: number, endYear: number, selectedYear: number) => {
  const start = new Date(startYear).getUTCFullYear();
  const end = new Date(endYear).getUTCFullYear();

  const isWithinSelectedYearRange =
    selectedYear === 0 ||
    (start >= selectedYear && start <= selectedYear) ||
    (end >= selectedYear && end <= selectedYear) ||
    (start < selectedYear && end > selectedYear);
  return isWithinSelectedYearRange;
};

export const mapUnitToCompanyCurrency = (
  unit: string | undefined | null,
  companyCurrency: string
) => {
  if (!unit) return 'NA';
  if (unit === 'currency') {
    return companyCurrency;
  } else if (unit.includes('Monetary unit')) {
    return unit.replace(/Monetary unit/g, companyCurrency);
  } else if (unit.includes('€')) {
    return unit.replace(/€/g, companyCurrency);
  } else return unit;
};

export const createGraphObject = (
  isYearly: boolean,
  q1: number,
  q2: number,
  q3: number,
  q4: number,
  year: number
) => {
  return isYearly
    ? {
        [QuartersEnums.q1]: 0,
        [QuartersEnums.q2]: 0,
        [QuartersEnums.q3]: 0,
        [QuartersEnums.q4]: 0,
        Year: year,
      }
    : {
        [QuartersEnums.q1]: q1,
        [QuartersEnums.q2]: q2,
        [QuartersEnums.q3]: q3,
        [QuartersEnums.q4]: q4,
      };
};

export const stringToNumber = (value: string | null | undefined) => {
  if (value == null && value == undefined) {
    return value;
  }
  return Number(value);
};

export const getDataCollectionText = (type: string, isGroup: boolean) => {
  if (type === DataCollectionLevel.company) return 'Company level';
  else if (type === DataCollectionLevel.reportingUnits) return 'Business units';
  else if (type === DataCollectionLevel.group && isGroup) return 'Group level';
  else if (type === DataCollectionLevel.group) return 'Parent company';
  else if (type === DataCollectionLevel.subsidiaries) return 'Subsidiaries';
  return 'NA';
};

export const isMetricStateMaterial = (isMaterial: null | boolean | undefined) => {
  // We should always consider a metric as material if its true or null, but not if its false
  if (isMaterial === undefined) return false;
  return isMaterial !== false;
};

export const orderCategories = (
  categories: GetEsrsCategoriesQuery_['EsrsCategory']
): GetFullEsrsCategoriesQuery_['EsrsCategory'] => {
  const orderedRefs = ['general', 'environment', 'social', 'governance'];
  const unorderedCategories = [...categories];
  const orderedCategories = unorderedCategories.sort((a, b) => {
    return orderedRefs.indexOf(a.reference) - orderedRefs.indexOf(b.reference);
  });
  return orderedCategories;
};

export const orderByESG = <T>(collection: T[], path: string | string[]): T[] => {
  const esgOrder = ['general', 'environment', 'social', 'governance'];
  return orderByList<T>(collection, path, esgOrder);
};

export const orderByList = <T>(
  collection: T[],
  path: string | string[],
  orderArr: Array<any>
): T[] => {
  return _.sortBy(collection, [
    (item) => {
      const rank = orderArr.indexOf(_.get(item, path));
      return rank === -1 ? Number.MAX_SAFE_INTEGER : rank;
    },
  ]);
};

export const percentageToNumber = (percentage: number | undefined, number: number) => {
  const percentageValue = Number(percentage) ?? 0;
  const numberValue = Number(number);
  const result =
    percentageValue > 0
      ? numberValue + (numberValue * percentageValue) / 100
      : numberValue - (numberValue * Math.abs(percentageValue)) / 100;
  return Number(result.toFixed(2));
};

export const numberToPercentage = (baseline: number, number: number) => {
  if (baseline === 0) return 0;
  const percentage = ((number - baseline) / baseline) * 100;
  return Number(percentage);
};

export const getIsMetricLocked = (
  unlockCondition: boolean | null | undefined,
  factValue: string
) => {
  const unlockTerm =
    unlockCondition === null ? '' : unlockCondition ? BooleanEnum.True : BooleanEnum.False;
  return unlockCondition !== null && factValue !== unlockTerm;
};

export const getTagsCombination = (tags: { tagType: string; tagValue: string }[]) =>
  tags.length === 0
    ? NOTAGCOMBINATION
    : tags
        .map((tag) => `${tag.tagType}|${tag.tagValue}`)
        .sort()
        .join(';;');

export const createTaskReference = (data: {
  assessmentId: string;
  metricRef: string;
  reportingUnitId: string;
  timeframe: string;
  tagCombination: string;
  mdrmRef?: string;
}) => {
  return `${data.assessmentId};;${data.metricRef};;${data.reportingUnitId};;${data.timeframe};;${data.tagCombination};;${data.mdrmRef}`;
};

export const getTaskDueDate = (
  tasks: { status: TaskStatusEnum_Enum_; dueDate?: string | null }[]
) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getUTCFullYear();
    return `${day} ${month} ${year}`;
  };

  const uncompletedTasks = tasks.filter((task) => task.status !== TaskStatusEnum_Enum_.Completed_);

  if (uncompletedTasks.length) {
    // Filter out tasks with null due dates
    const tasksWithDates = uncompletedTasks.filter((task) => task.dueDate !== null);

    // If no tasks have dates, return "No deadline"
    if (tasksWithDates.length === 0) {
      return { date: 'No deadline', state: 'current' };
    }

    // Find the task with the closest due date
    const closestTask = tasksWithDates.reduce((closest, current) => {
      return new Date(current.dueDate!) < new Date(closest.dueDate!) ? current : closest;
    });

    const closestDate = new Date(closestTask.dueDate!);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare dates only

    // Check if the closest date is overdue
    if (closestDate < today) {
      return { date: formatDate(closestTask.dueDate!), state: 'overdue' };
    }

    return { date: formatDate(closestTask.dueDate!), state: 'current' };
  }

  // All tasks are completed
  if (tasks.length > 0) {
    const tasksWithDates = tasks.filter((task) => task.dueDate !== null);

    // If no completed tasks have dates, return "No deadline"
    if (tasksWithDates.length === 0) {
      return { date: 'No deadline', state: 'current' };
    }

    const closestCompletedTask = tasksWithDates.reduce((closest, current) => {
      return new Date(current.dueDate!) < new Date(closest.dueDate!) ? current : closest;
    });

    return { date: formatDate(closestCompletedTask.dueDate!), state: 'completed' };
  }

  // No tasks at all
  return { date: 'No deadline', state: 'current' };
};
